import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Alert, 
  CircularProgress, 
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton
} from '@mui/material';
import { Add as AddIcon, ContentCopy as CopyIcon } from '@mui/icons-material';


interface Account {
  username: string;
  password: string;
}

// List of usernames created is not going to be stored in the redux store
// Not sure how you would want to handle this ???
export default function AdminPage() {
  const [numberOfAccounts, setNumberOfAccounts] = useState<number>(1);
  const [defaultPassword, setDefaultPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [createdAccounts, setCreatedAccounts] = useState<Account[]>([]);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);


  const validatePassword = (password: string) => {
    if (password.trim() && password.trim().length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const createBulkAccounts = async () => {
    if (numberOfAccounts < 1 || numberOfAccounts > 100) {
      setError('Please enter a number between 1 and 100');
      return;
    }
    
    if (!validatePassword(defaultPassword)) {
      return;
    }

    setIsCreating(true);
    setError('');
    setCreatedAccounts([]);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/users/create-bulk-accounts`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numberOfAccounts: numberOfAccounts,
          defaultPassword: defaultPassword.trim() || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create accounts');
      }

      const data = await response.json();
      setCreatedAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create student accounts. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }

  const handleCreateAccounts = async () => {
    await createBulkAccounts();
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(`Failed to copy ${type}:`, err);
    }
  };

  const copyTableToClipboard = async () => {
    try {
      // Create tab-separated values format
      const headers = 'Username\tDefault Password';
      const rows = createdAccounts.map(account => 
        `${account.username}\t${account.password}`
      ).join('\n');
      const tableText = `${headers}\n${rows}`;
      
      await navigator.clipboard.writeText(tableText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Hide success message after 2 seconds
    } catch (err) {
      console.error('Failed to copy table:', err);
      setError('Failed to copy table to clipboard');
    }
  };

  return (
    <Box >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.primary">
          Bulk Student Account Creation
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Create Student Accounts
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Number of Accounts"
              type="number"
              value={numberOfAccounts}
              onChange={(e) => setNumberOfAccounts(Number(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              helperText="Enter a number between 1 and 100"
              disabled={isCreating}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Default Password (optional)"
              type="password"
              value={defaultPassword}
              onChange={(e) => {
                setDefaultPassword(e.target.value);
                validatePassword(e.target.value);
              }}
              error={!!passwordError}
              helperText={passwordError || "Minimum 6 characters - Leave empty to generate a random password"}
              disabled={isCreating}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleCreateAccounts}
            disabled={isCreating || numberOfAccounts < 1 || numberOfAccounts > 100 || !!passwordError}
            startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ mb: 3 , backgroundColor: "primary.main" , "&:hover": { backgroundColor: "primary.dark" } , color: "text.primary" }}
          >
            {isCreating ? 'Creating...' : 'Create Accounts'}
          </Button>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Progress Info */}
          {isCreating && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={40} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Creating {numberOfAccounts} student account{numberOfAccounts > 1 ? 's' : ''}...
              </Typography>
            </Box>
          )}

          {/* Results */}
          {createdAccounts.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Created Accounts ({createdAccounts.length})
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={copyTableToClipboard}
                  sx={{ ml: 2, backgroundColor: "primary.main" }}
                >
                  Copy Table
                </Button>
              </Box>
              
              {copySuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Table copied to clipboard successfully!
                </Alert>
              )}
              <Paper variant="outlined" sx={{
                width: '100%',
                overflowX: 'auto'
              }}>
                <Table stickyHeader sx={{ 
                  minWidth: 400,
                  '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': { 
                    borderBottom: 0,
                    paddingBottom: 1,
                  } 
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 'bold',
                        backgroundColor: 'none',
                        color: 'text.primary'
                      }}>Username</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold',
                        backgroundColor: 'none',
                        color: 'text.primary'
                      }}>Default Password</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {createdAccounts.map((account, index) => (
                      <TableRow key={index}>
                        <TableCell sx = {{ display: 'flex', alignItems: 'center' }}>
                          {account.username}
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(account.username, 'username')}
                            sx={{ ml: '1.2rem', display: { xs: 'none', sm: 'inline-flex' } }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {account.password}
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(account.password, 'password')}
                            sx={{ ml: '1.2rem', display: { xs: 'none', sm: 'inline-flex' } }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}